import WalletBalanceAction from "./WalletBalanceAction";
import React, { useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import {
  daysLeft,
  dueKind,
  matchesQuery,
  money,
  readQuery,
  renewPriceOf,
} from "./helpers";
import GetAppIcon from "@material-ui/icons/GetApp";
import {
  ContactAvatar,
  EmptyHint,
  FilterChips,
  LoadingHint,
  NegocioPager,
  PageFrame,
  SearchField,
  stopRow,
  tNegocio,
  useMobile,
  usePager,
} from "./shared";
import { AccountPeek, ProductMark } from "./AccountPeek";
import { AddCartItemDialog, CartConfigDialog, CartDetailDialog, CartRowActions } from "./CartDialog";
import { CustomerServiceDialog, SendPromptDialog } from "./CustomerServiceDialog";
import { useRecords } from "./Lists";
import { useStyles } from "./styles";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { openBinancePaymentFromNotify } from "./BinanceCheckoutDialog";
import { toast } from "react-toastify";

const DUE_STATUSES = ["soon", "today", "expired"];

const collectDueCaption = (g) => {
  if (g.expiredCount > 0) {
    if (g.maxOverdueDays > 0) {
      return tNegocio("pages.subscriptions.expiredOverdue")
        .replace("{count}", String(g.expiredCount))
        .replace("{days}", String(g.maxOverdueDays));
    }
    return `${g.expiredCount} ${tNegocio("pages.subscriptions.expiredShort")}`;
  }
  const left = g.minDaysLeft;
  if (left == null) return "";
  if (left === 0) return tNegocio("due.today");
  if (left === 1) return tNegocio("due.tomorrow");
  return tNegocio("due.leftN").replace("{n}", String(left));
};

export default function CollectionsPage() {
  const classes = useStyles();
  const isMobile = useMobile();
  const location = useLocation();
  const { records, loading, reload } = useRecords("subscriptions");
  const customers = useRecords("customers");
  const [payments, setPayments] = useState([]);
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("");
  const [party, setParty] = useState("");
  const [cartId, setCartId] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [peekAccountId, setPeekAccountId] = useState(null);
  const [renewSub, setRenewSub] = useState(null);
  const [sendPrompt, setSendPrompt] = useState(null);
  const batchSinceRef = useRef(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const ensurePayments = async () => {
    if (paymentsLoaded) return payments;
    try {
      const { data } = await api.get("/negocio/payment-methods");
      const rows = data.records || [];
      setPayments(rows);
      setPaymentsLoaded(true);
      return rows;
    } catch (e) {
      toastError(e);
      return [];
    }
  };

  const refresh = async () => {
    setNow(Date.now());
    await Promise.all([reload({ soft: true }), customers.reload({ soft: true })]);
  };

  React.useEffect(() => {
    if (!cartId && !configOpen && !addOpen) return undefined;
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, [cartId, configOpen, addOpen]);

  React.useEffect(() => {
    const params = readQuery(location.search);
    if (params.contactId) setCartId(Number(params.contactId));
  }, [location.search]);

  React.useEffect(() => {
    if (cartId || configOpen || addOpen) ensurePayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, configOpen, addOpen]);

  const customerByContact = useMemo(() => {
    const map = new Map();
    (customers.records || []).forEach((c) => map.set(Number(c.contactId), c));
    return map;
  }, [customers.records]);

  const groups = useMemo(() => {
    const byContact = new Map();
    records.forEach((sub) => {
      const status = dueKind(sub);
      if (!DUE_STATUSES.includes(status)) return;
      const contactId = Number(sub.contactId || sub.contact?.id);
      if (!contactId) return;
      if (!byContact.has(contactId)) {
        const customer = customerByContact.get(contactId);
        byContact.set(contactId, {
          contactId,
          name: sub.contact?.name || customer?.name || "—",
          number: sub.contact?.number || customer?.number || "",
          customerType: customer?.customerType || "final",
          code: customer?.code || "",
          items: [],
          soonCount: 0,
          todayCount: 0,
          expiredCount: 0,
          amountDue: 0,
        });
      }
      const group = byContact.get(contactId);
      const sale = sub.sale;
      const price = renewPriceOf(sub);
      group.items.push({ ...sub, collectStatus: status, pendingPay: sale?.status === "pending_payment", renewPrice: price });
      group.amountDue += price;
      if (status === "soon") group.soonCount += 1;
      else if (status === "today") group.todayCount += 1;
      else group.expiredCount += 1;
    });
    return Array.from(byContact.values()).map((g) => {
      const maxOverdueDays = (g.items || []).reduce((max, item) => {
        if (item.collectStatus !== "expired") return max;
        const left = daysLeft(item.endDate);
        if (left == null || left >= 0) return max;
        const overdue = Math.abs(left);
        return overdue > max ? overdue : max;
      }, 0);
      const minDaysLeft = (g.items || []).reduce((min, item) => {
        const left = daysLeft(item.endDate);
        if (left == null || left < 0) return min;
        return min == null || left < min ? left : min;
      }, null);
      const soonestEnd = (g.items || []).reduce((min, item) => {
        const end = String(item.endDate || "").slice(0, 10);
        if (!end) return min;
        return !min || end < min ? end : min;
      }, "");
      return {
        ...g,
        amount: g.amountDue,
        maxOverdueDays,
        minDaysLeft,
        soonestEnd,
        items: [...g.items].sort((a, b) => {
          const STATUS_RANK = { expired: 0, today: 1, soon: 2 };
          const ra = STATUS_RANK[a.collectStatus] ?? 9;
          const rb = STATUS_RANK[b.collectStatus] ?? 9;
          if (ra !== rb) return ra - rb;
          return String(a.endDate || "").localeCompare(String(b.endDate || ""));
        }),
      };
    });
  }, [records, customerByContact]);

  const rows = useMemo(
    () =>
      groups
        .map((g) => {
          if (party && g.customerType !== party) return null;
          const items = kind ? g.items.filter((item) => item.collectStatus === kind) : g.items;
          if (!items.length) return null;
          const productNames = items.map((item) => item.product?.name);
          if (!matchesQuery(g, query, [g.name, g.number, g.code, ...productNames])) return null;
          if (!kind) return g;
          const amountDue = items.reduce((n, item) => n + renewPriceOf(item), 0);
          return {
            ...g,
            items,
            soonCount: items.filter((item) => item.collectStatus === "soon").length,
            todayCount: items.filter((item) => item.collectStatus === "today").length,
            expiredCount: items.filter((item) => item.collectStatus === "expired").length,
            maxOverdueDays: items.reduce((max, item) => {
              if (item.collectStatus !== "expired") return max;
              const left = daysLeft(item.endDate);
              if (left == null || left >= 0) return max;
              const overdue = Math.abs(left);
              return overdue > max ? overdue : max;
            }, 0),
            minDaysLeft: items.reduce((min, item) => {
              const left = daysLeft(item.endDate);
              if (left == null || left < 0) return min;
              return min == null || left < min ? left : min;
            }, null),
            amountDue,
            amount: amountDue,
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          const STATUS_RANK = { expired: 0, today: 1, soon: 2 };
          const worst = (items) =>
            (items || []).reduce((best, item) => {
              const rank = STATUS_RANK[item.collectStatus] ?? 9;
              return rank < best ? rank : best;
            }, 9);
          const wa = worst(a.items);
          const wb = worst(b.items);
          if (wa !== wb) return wa - wb;
          const soonest = (items) =>
            (items || []).reduce((min, item) => {
              const end = String(item.endDate || "").slice(0, 10);
              if (!end) return min;
              return !min || end < min ? end : min;
            }, "");
          const ea = soonest(a.items);
          const eb = soonest(b.items);
          if (ea && eb && ea !== eb) return ea.localeCompare(eb);
          if (ea && !eb) return -1;
          if (!ea && eb) return 1;
          return String(a.name || "").localeCompare(String(b.name || ""));
        }),
    [groups, query, kind, party]
  );

  const pager = usePager(rows);

  const kpis = useMemo(() => {
    const soon = groups.reduce((n, g) => n + g.soonCount, 0);
    const today = groups.reduce((n, g) => n + g.todayCount, 0);
    const expired = groups.reduce((n, g) => n + g.expiredCount, 0);
    const amount = groups.reduce((n, g) => n + g.amountDue, 0);
    return { amount, soon, today, expired };
  }, [groups]);

  const finalCount = groups.filter((g) => g.customerType !== "reseller").length;
  const resellerCount = groups.filter((g) => g.customerType === "reseller").length;
  const cartGroup = rows.find((g) => Number(g.contactId) === Number(cartId)) || groups.find((g) => Number(g.contactId) === Number(cartId));
  const cartCustomer = customerByContact.get(Number(cartId)) || null;

  const notifyCart = async ({ kind: notifyKind, channel, contactId, sinceUnix, subscriptionIds }) => {
    try {
      const { data } = await api.post("/negocio/notify", {
        notify: true,
        cart: true,
        kind: notifyKind,
        channel,
        contactId,
        ...(sinceUnix != null ? { sinceUnix } : {}),
        ...(subscriptionIds?.length ? { subscriptionIds } : {}),
      });
      toast.success(tNegocio("pages.customers.sentOk"));
      openBinancePaymentFromNotify(data);
    } catch (e) {
      toastError(e);
    }
  };

  const openSendLast = (customer, subscriptionIds = []) => {
    if (!customer) return;
    if (batchSinceRef.current == null) batchSinceRef.current = { contactId: customer.contactId, ids: [] };
    const batch = batchSinceRef.current;
    batch.ids = [...new Set([...batch.ids, ...subscriptionIds])];
    setSendPrompt({ kind: "last", customer, cart: true, subscriptionIds: batch.ids });
  };

  const closeSendPrompt = () => {
    setSendPrompt(null);
    batchSinceRef.current = null;
  };

  const notifyCustomer = async ({ kind: notifyKind, channel, contactId, subscriptionId }) => {
    try {
      const { data } = await api.post("/negocio/notify", {
        notify: true,
        kind: notifyKind,
        channel,
        contactId,
        subscriptionId,
      });
      toast.success(tNegocio("pages.customers.sentOk"));
      openBinancePaymentFromNotify(data);
    } catch (e) {
      toastError(e);
    }
  };

  const sendAllReminders = async () => {
    if (sendingAll) return;
    setSendingAll(true);
    try {
      const { data } = await api.post("/negocio/reminders/dispatch", { force: false });
      if (data?.running) {
        toast.info(tNegocio("pages.subscriptions.remindersRunning"));
      } else if (data?.queued) {
        toast.success(
          tNegocio("pages.subscriptions.remindersSent").replace("{n}", String(data?.count || 0))
        );
      } else {
        const lines = (data?.results || [])
          .map(
            (r) =>
              `${r.name}: ${r.sent} enviados` +
              (r.matched != null ? ` (${r.matched} match)` : "") +
              (r.errors ? `, ${r.errors} error(es)` : "")
          )
          .join(" · ");
        toast.success(
          tNegocio("pages.subscriptions.remindersReport")
            .replace("{sent}", String(data?.sent || 0))
            .replace("{detail}", lines || "—")
        );
      }
    } catch (e) {
      toastError(e);
    } finally {
      setSendingAll(false);
    }
  };

  const exportReport = () => {
    const header = [
      "cliente",
      "codigo",
      "telefono",
      "tipo",
      "servicio",
      "cuenta",
      "vence",
      "dias",
      "estado",
      "monto",
    ];
    const statusLabel = (s) => {
      if (s === "soon") return tNegocio("pages.subscriptions.soonShort");
      if (s === "today") return tNegocio("pages.subscriptions.todayShort");
      if (s === "expired") return tNegocio("status.expired");
      return s || "";
    };
    const lines = [];
    rows.forEach((g) => {
      (g.items || []).forEach((item) => {
        const left = daysLeft(item.endDate);
        lines.push(
          [
            g.name,
            g.code,
            g.number,
            g.customerType,
            item.product?.name || "",
            item.account?.email || "",
            String(item.endDate || "").slice(0, 10),
            left == null ? "" : String(left),
            statusLabel(item.collectStatus),
            String(item.renewPrice || 0),
          ]
            .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
            .join(",")
        );
      });
    });
    const blob = new Blob([`\uFEFF${header.join(",")}\n${lines.join("\n")}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cobros-${kind || "todos"}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageFrame
        lockBody
        title={tNegocio("pages.subscriptions.title")}
        subtitle={tNegocio("pages.subscriptions.subtitle")}
        actions={
          <>
            <Button startIcon={<GetAppIcon />} onClick={exportReport} disabled={!rows.length}>
              {tNegocio("pages.subscriptions.exportReport")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<NotificationsActiveIcon />}
              disabled={sendingAll}
              onClick={sendAllReminders}
            >
              {tNegocio("pages.subscriptions.sendReminders")}
            </Button>
          </>
        }
      >
        <div className={classes.kpiGrid}>
          <div className={classes.kpiCard} onClick={() => setKind("soon")}>
            <div className={classes.kpiLabel}>{tNegocio("pages.subscriptions.kpis.soon")}</div>
            <div className={classes.kpiValue}>{loading ? "…" : kpis.soon}</div>
          </div>
          <div className={classes.kpiCard} onClick={() => setKind("today")}>
            <div className={classes.kpiLabel}>{tNegocio("pages.subscriptions.kpis.today")}</div>
            <div className={classes.kpiValue}>{loading ? "…" : kpis.today}</div>
          </div>
          <div className={classes.kpiCard} onClick={() => setKind("expired")}>
            <div className={classes.kpiLabel}>{tNegocio("pages.subscriptions.kpis.expired")}</div>
            <div className={classes.kpiValue}>{loading ? "…" : kpis.expired}</div>
          </div>
          <div className={classes.kpiCard} onClick={() => setKind("")}>
            <div className={classes.kpiLabel}>{tNegocio("pages.subscriptions.kpis.amount")}</div>
            <div className={classes.kpiValue}>{loading ? "…" : money(kpis.amount)}</div>
          </div>
        </div>

        <div className={classes.filterBar}>
          <FilterChips
            value={party}
            onChange={setParty}
            options={[
              { value: "", label: `${tNegocio("filters.all")} (${groups.length})` },
              { value: "final", label: `${tNegocio("pages.customers.types.final")} (${finalCount})` },
              { value: "reseller", label: `${tNegocio("pages.customers.types.reseller")} (${resellerCount})` },
            ]}
          />
          <span className={classes.filterSplit} />
          <FilterChips
            value={kind}
            onChange={setKind}
            options={[
              { value: "", label: tNegocio("filters.all") },
              { value: "soon", label: tNegocio("pages.subscriptions.soonShort") },
              { value: "today", label: tNegocio("pages.subscriptions.todayShort") },
              { value: "expired", label: tNegocio("status.expired") },
            ]}
          />
        </div>
        <SearchField fullWidth value={query} onChange={setQuery} placeholder={tNegocio("pages.subscriptions.search")} />

        {rows.length ? (
          <div className={`${classes.tableWrap} ${classes.tableWrapFill}`}>
            {pager.slice.map((g, index) => {
              const customer = customerByContact.get(g.contactId);
              const dueCaption = collectDueCaption(g);
              const marks = (
                <div className={classes.collectMarks} onClick={(e) => e.stopPropagation()}>
                  {(g.items || []).map((item) => (
                    <Tooltip
                      key={item.id}
                      title={`${item.product?.name || tNegocio("renew")}${item.endDate ? ` · ${item.endDate}` : ""}`}
                    >
                      <span
                        role="button"
                        tabIndex={0}
                        style={{ display: "inline-flex", cursor: "pointer", padding: isMobile ? 4 : 0 }}
                        onClick={stopRow(() => setRenewSub(item))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            setRenewSub(item);
                          }
                        }}
                      >
                        <ProductMark product={item.product} size={isMobile ? 32 : 28} circle link={false} />
                      </span>
                    </Tooltip>
                  ))}
                </div>
              );
              const amount = (
                <div className={classes.collectAmount}>
                  <div style={{ fontWeight: 800 }}>{money(g.amountDue)}</div>
                  <div className={classes.captionBlock}>
                    {g.items.length}
                    {isMobile && dueCaption ? ` · ${dueCaption}` : ""}
                  </div>
                </div>
              );
              const actions = customer ? (
                <div className={classes.serviceCardActions} onClick={(event) => event.stopPropagation()}>
                  <WalletBalanceAction customer={customer} />
                  <CartRowActions
                    cart={g}
                    customer={customer}
                    now={now}
                    collections
                    onChanged={async () => {
                      await refresh();
                      if (Number(cartId) === Number(g.contactId)) setCartId(null);
                    }}
                    onNotifyCart={notifyCart}
                    onRenewed={() => setSendPrompt({ kind: "renewed", customer, cart: true })}
                  />
                </div>
              ) : null;
              return (
                <div
                  key={g.contactId}
                  className={`${classes.clickableRow} ${classes.serviceCustomerCard}`}
                  style={{ animationDelay: `${Math.min(index, 5) * 35}ms` }}
                  onClick={() => setCartId(g.contactId)}
                  onKeyDown={(event) => {
                    if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); setCartId(g.contactId); }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className={classes.collectRow}>
                    <div className={classes.collectIdentity}>
                      <ContactAvatar name={g.name} number={g.number} profilePicUrl={customer?.profilePicUrl} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800 }}>{g.name}</div>
                        <div className={classes.captionBlock}>
                          {g.code}
                          {g.number ? ` · ${g.number}` : ""}
                          {!isMobile && dueCaption ? ` · ${dueCaption}` : ""}
                        </div>
                      </div>
                    </div>
                    {isMobile ? (
                      <>
                        <div className={classes.collectMetaRow}>
                          {marks}
                          {amount}
                        </div>
                        {actions}
                      </>
                    ) : (
                      <>
                        {marks}
                        {amount}
                        {actions}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            <NegocioPager
              count={pager.count}
              page={pager.page}
              rowsPerPage={pager.rowsPerPage}
              onChangePage={pager.setPage}
              onChangeRowsPerPage={pager.setRowsPerPage}
            />
          </div>
        ) : loading ? (
          <LoadingHint />
        ) : (
          <EmptyHint
            title={tNegocio("pages.subscriptions.emptyTitle")}
            subtitle={tNegocio("pages.subscriptions.empty")}
          />
        )}
      </PageFrame>
      <CartConfigDialog
        open={configOpen}
        customers={customers.records || []}
        payments={payments}
        preset={cartCustomer}
        onClose={() => setConfigOpen(false)}
        onSaved={async (contactId) => {
          setConfigOpen(false);
          await customers.reload();
          setCartId(contactId);
        }}
      />
      <CartDetailDialog
        open={!!cartId && !configOpen && !addOpen}
        cart={cartGroup}
        customer={cartCustomer}
        payments={payments}
        now={now}
        onClose={() => setCartId(null)}
        onEditConfig={() => {
          setConfigOpen(true);
        }}
        onAddItem={() => setAddOpen(true)}
        onChanged={refresh}
        onNotify={notifyCustomer}
        onNotifyCart={notifyCart}
        onPeekAccount={setPeekAccountId}
        onRenewed={(payload) => setSendPrompt(payload)}
        collections
      />
      <AddCartItemDialog
        open={addOpen}
        customer={cartCustomer}
        payments={payments}
        cartHasItems={!!(cartGroup?.items || []).length || batchSinceRef.current != null}
        onClose={() => setAddOpen(false)}
        onSaved={(result) => {
          const customer = cartCustomer;
          setAddOpen(false);
          setNow(Date.now());
          openSendLast(customer, result?.subscriptionIds || []);
          refresh();
        }}
      />
      <CustomerServiceDialog
        open={!!renewSub}
        mode="renew"
        sub={renewSub}
        customer={
          renewSub
            ? customerByContact.get(Number(renewSub.contactId || renewSub.contact?.id)) ||
              customers.records.find((c) => Number(c.contactId) === Number(renewSub.contactId))
            : null
        }
        onClose={() => setRenewSub(null)}
        onSaved={async (mode) => {
          const contactId = renewSub?.contactId;
          setRenewSub(null);
          if (mode === "renew" && contactId) {
            const customer =
              customerByContact.get(Number(contactId)) ||
              customers.records.find((c) => Number(c.contactId) === Number(contactId));
            if (customer) setSendPrompt({ kind: "renewed", customer, cart: true });
          }
          refresh();
        }}
      />
      <AccountPeek
        open={!!peekAccountId}
        accountId={peekAccountId}
        onClose={() => setPeekAccountId(null)}
        onChanged={refresh}
      />
      <SendPromptDialog
        prompt={sendPrompt}
        onClose={closeSendPrompt}
        onAddAnother={(prompt) => {
          setSendPrompt(null);
          if (prompt?.customer) {
            setCartId(Number(prompt.customer.contactId));
            setAddOpen(true);
          }
        }}
        onSend={async (prompt, channel) => {
          if (prompt.cart || ["order", "last", "reminder", "update", "renewed"].includes(prompt.kind)) {
            await notifyCart({
              kind: prompt.kind || "last",
              channel,
              contactId: prompt.customer?.contactId,
              subscriptionIds: prompt.subscriptionIds,
            });
          } else {
            await notifyCustomer({
              kind: prompt.kind || "renew",
              channel,
              contactId: prompt.customer?.contactId,
              subscriptionId: prompt.subscriptionId,
            });
          }
          closeSendPrompt();
        }}
      />
    </>
  );
}
